import './CoralAnimatedCTA.scss'

import { useIsWindowBiggerThan } from '@bob/library'
import { matomo } from '@websrc/App/Tracking/Matomo'
import { mapCTA } from '@websrc/DataMappers/mapCTA'
import React, { useEffect, useRef, useState } from 'react'

import { Image } from '../Asset/Image/Image'
import { CTA } from '../CTA'
import ImageA from './Assets/image-a.webp'
import ImageB from './Assets/image-b.webp'
import ImageC from './Assets/image-c.webp'
import ImageD from './Assets/image-d.webp'
import ImageMobile from './Assets/image-mobile.webp'

type CoralAnimatedCTAProps = {
  headline?: string | null
  description?: string | null
  cta?: ReturnType<typeof mapCTA> | null
}

type Breakpoint = 'tablet' | 'desktop' | 'widescreen'

type ImagePosition = {
  left?: number
  right?: number
  top?: number
  bottom?: number
  rotate?: number
}

interface ImageConfig {
  start: Record<Breakpoint, ImagePosition>
  end: Record<Breakpoint, ImagePosition>
}

const imageConfigs: ImageConfig[] = [
  {
    start: {
      tablet: { left: -274.707, bottom: -115.823, rotate: -5 },
      desktop: { left: -204.706, bottom: -94.823, rotate: -5 },
      widescreen: { left: -173.642, bottom: -170.939, rotate: -5 },
    },
    end: {
      tablet: { left: -277.394, bottom: -119.782, rotate: -11 },
      desktop: { left: -223.315, bottom: -54.535, rotate: -11 },
      widescreen: { left: -196.36, bottom: -135.004, rotate: -11 },
    },
  },
  {
    start: {
      tablet: { right: 20, top: 120.059, rotate: -37.924 },
      desktop: { right: 20, top: 150.059, rotate: -37.924 },
      widescreen: { right: 60.577, top: 160, rotate: -20.962 },
    },
    end: {
      tablet: { right: 20, top: 22.914, rotate: -23.924 },
      desktop: { right: 20, top: 70.059, rotate: -23.924 },
      widescreen: { right: 60.577, top: 20, rotate: -5.962 },
    },
  },
  {
    start: {
      tablet: { right: 14.019, bottom: 5.464, rotate: 15.922 },
      desktop: { right: 14.019, bottom: 5.464, rotate: 15.922 },
      widescreen: { right: 53.38, bottom: -65.863, rotate: -25.962 },
    },
    end: {
      tablet: { right: 14.019, bottom: 100.463, rotate: 17.922 },
      desktop: { right: 14.019, bottom: 100.464, rotate: 17.922 },
      widescreen: { right: 53.38, bottom: 55.137, rotate: -5.962 },
    },
  },
  {
    start: {
      tablet: { right: 94, bottom: -8.565, rotate: -37.341 },
      desktop: { right: 94, bottom: 11.565, rotate: -37.341 },
      widescreen: { right: 170, bottom: -88, rotate: -55.097 },
    },
    end: {
      tablet: { right: 94, bottom: 75.565, rotate: -22.341 },
      desktop: { right: 94, bottom: 88.565, rotate: -22.341 },
      widescreen: { right: 170, bottom: 22, rotate: -41.097 },
    },
  },
]

export const CoralAnimatedCTA: React.FC<CoralAnimatedCTAProps> = (props) => {
  const mobileImgRef = useRef<HTMLImageElement>(null)
  const [mobileImageOffset, setMobileImageOffset] = useState(0)
  const [imageAStyle, setImageAStyle] = useState<React.CSSProperties>({})
  const [imageBStyle, setImageBStyle] = useState<React.CSSProperties>({})
  const [imageCStyle, setImageCStyle] = useState<React.CSSProperties>({})
  const [imageDStyle, setImageDStyle] = useState<React.CSSProperties>({})
  const containerRef = useRef<HTMLDivElement>(null)
  const isDesktop = useIsWindowBiggerThan(1079)
  const isWidescreen = useIsWindowBiggerThan(1439)
  function lerp(start: number, end: number, t: number) {
    return start + (end - start) * t
  }

  useEffect(() => {
    const handleScroll = () => {
      if (!containerRef.current) return
      const rect = containerRef.current.getBoundingClientRect()
      const windowHeight = window.innerHeight || document.documentElement.clientHeight
      const rawProgress = 1 - rect.top / windowHeight
      const progress = Math.min(Math.max(rawProgress * 1, 0), 1)

      function getStyle(start: ImagePosition, end: ImagePosition): React.CSSProperties {
        const style: React.CSSProperties = {}
        if ('left' in start && 'left' in end)
          style.left = `${lerp(start.left ?? 0, end.left ?? 0, progress)}px`
        if ('right' in start && 'right' in end)
          style.right = `${lerp(start.right ?? 0, end.right ?? 0, progress)}px`
        if ('top' in start && 'top' in end)
          style.top = `${lerp(start.top ?? 0, end.top ?? 0, progress)}px`
        if ('bottom' in start && 'bottom' in end)
          style.bottom = `${lerp(start.bottom ?? 0, end.bottom ?? 0, progress)}px`
        if ('rotate' in start && 'rotate' in end)
          style.transform = `rotate(${lerp(start.rotate ?? 0, end.rotate ?? 0, progress)}deg)`
        return style
      }

      let viewport: Breakpoint = 'tablet'
      if (isWidescreen) viewport = 'widescreen'
      else if (isDesktop) viewport = 'desktop'

      const configA = imageConfigs[0]
      const startA = configA.start[viewport]
      const endA = configA.end[viewport]
      setImageAStyle(getStyle(startA, endA))

      const configB = imageConfigs[1]
      const startB = configB.start[viewport]
      const endB = configB.end[viewport]
      setImageBStyle(getStyle(startB, endB))

      const configC = imageConfigs[2]
      const startC = configC.start[viewport]
      const endC = configC.end[viewport]
      setImageCStyle(getStyle(startC, endC))

      const configD = imageConfigs[3]
      const startD = configD.start[viewport]
      const endD = configD.end[viewport]
      setImageDStyle(getStyle(startD, endD))
    }

    window.addEventListener('scroll', handleScroll, { passive: true })
    handleScroll()
    return () => window.removeEventListener('scroll', handleScroll)
  }, [isDesktop, isWidescreen])

  useEffect(() => {
    const handleScroll = () => {
      if (!mobileImgRef.current) return
      const rect = mobileImgRef.current.getBoundingClientRect()
      const windowHeight = window.innerHeight || document.documentElement.clientHeight
      const rawProgress = 1 - rect.top / windowHeight
      const progress = Math.min(Math.max(rawProgress * 1, 0), 1)
      setMobileImageOffset(progress * 50)
    }
    window.addEventListener('scroll', handleScroll, { passive: true })
    handleScroll()
    return () => window.removeEventListener('scroll', handleScroll)
  }, [])

  return (
    <div className={'category-coral-spot'}>
      <div className={'coral-animated-cta'}>
        <div className={'coral-animated-cta__container'} ref={containerRef}>
          <div className={'coral-animated-cta__body'}>
            <div className={'coral-animated-cta__mobile-image-container'}>
              <img
                alt={'Flowers'}
                className={'coral-animated-cta__mobile-image'}
                ref={mobileImgRef}
                src={ImageMobile}
                style={{ top: `${mobileImageOffset}px` }}
              />
            </div>
            <div className={'coral-animated-cta__header'}>{props?.headline}</div>
            <div className={'coral-animated-cta__description'}>{props?.description}</div>
            <div className={'coral-animated-cta__button'}>
              <CTA
                className={`coral-global-button m--type-fill m--theme-white ${
                  isDesktop ? 'm--size-lg' : 'm--size-md'
                }`}
                cta={props.cta ?? null}
                onClick={() => {
                  matomo.trackEvent(
                    'engagement',
                    'all',
                    [props?.headline],
                    [props?.cta?.text],
                    'button'
                  )
                }}
                type={'clean'}
              />
              <div />
            </div>
          </div>
          <Image
            alt={'LEGO models from LEGO Education lessons'}
            className={'coral-animated-cta__absolute-image m--image-a'}
            lazy={false}
            src={ImageA}
            style={imageAStyle}
          />
          <Image
            alt={'LEGO models from LEGO Education lessons'}
            className={'coral-animated-cta__absolute-image m--image-b'}
            lazy={true}
            src={ImageB}
            style={imageBStyle}
          />
          <Image
            alt={'LEGO models from LEGO Education lessons'}
            className={'coral-animated-cta__absolute-image m--image-c'}
            lazy={true}
            src={ImageC}
            style={imageCStyle}
          />
          <Image
            alt={'LEGO models from LEGO Education lessons'}
            className={'coral-animated-cta__absolute-image m--image-d'}
            lazy={true}
            src={ImageD}
            style={imageDStyle}
          />
        </div>
      </div>
    </div>
  )
}
